import type { MonospaceSerializedPrimitiveField } from '#layers/data-model/types/schema/primitive-field';
import type { SchemaManifest, SchemaManifestPrimitiveTypeRef, SchemaManifestStringRef } from '#layers/data-model/types/schema/schema-manifest';
import type {
	MonospaceDataSource,
	MonospaceIndex,
	MonospaceIndexField,
	MonospaceNamespace,
	MonospaceSerializedCollection,
	MonospaceSingleConstraint,
	MonospaceSingleConstraintField,
	MonospaceSingleRelationField,
} from '#layers/data-model/types/schema/workspace-schema';
import { SupportedSchemaManifestFormatVersion } from '#layers/data-model/types/schema/schema-manifest';

/**
 * The workspace-schema rows encoded in a schema manifest, in the exact shapes
 * the rstore system collections hold — what the deep `/items` bootstrap query
 * used to return, decoded from one document instead.
 */
export interface WorkspaceSchemaRows {
	dataSources: MonospaceDataSource[];
	namespaces: MonospaceNamespace[];
	collections: MonospaceSerializedCollection[];
	primitiveFields: MonospaceSerializedPrimitiveField[];
	relationFields: MonospaceSingleRelationField[];
	constraints: MonospaceSingleConstraint[];
	constraintFields: MonospaceSingleConstraintField[];
	indexes: MonospaceIndex[];
	indexFields: MonospaceIndexField[];
}

/**
 * Decodes the engine's schema manifest into flat workspace-schema rows.
 *
 * The document interns every name into its `strings` pool and cross-references
 * entities by array position; this resolves both back into the id-linked rows
 * the rest of the app is built on. Pure: rstore integration is the caller's.
 */
export function decodeSchemaManifest(
	document: SchemaManifest,
	dataSources: readonly (Pick<MonospaceDataSource, 'id' | 'preset'> & Partial<Pick<MonospaceDataSource, 'introspectionExclusions'>>)[],
): WorkspaceSchemaRows {
	if (document.formatVersion !== SupportedSchemaManifestFormatVersion) {
		throw new Error(
			`Unsupported schema manifest format version ${document.formatVersion}; this Studio build supports version ${SupportedSchemaManifestFormatVersion}`,
		);
	}

	const { strings, primitiveTypes, monospaceSchema } = document;
	const { sources, namespaces, collections } = monospaceSchema;
	// Presets are presentation metadata and are not held in the engine's schema.
	const metadata = new Map(dataSources.map((source) => [source.id, source]));

	const string = (index: SchemaManifestStringRef) => at(strings, index, 'string');
	const primitiveType = (index: SchemaManifestPrimitiveTypeRef) => at(primitiveTypes, index, 'primitive type');

	const rows: WorkspaceSchemaRows = {
		dataSources: sources.map((source) => ({
			id: source.id,
			apiName: string(source.name),
			provider: string(source.provider),
			preset: metadata.get(source.id)?.preset ?? null,
			introspectionExclusions: metadata.get(source.id)?.introspectionExclusions ?? null,
		})),
		namespaces: namespaces.map((namespace) => ({
			id: namespace.id,
			dbName: string(namespace.dbName),
			sourceId: at(sources, namespace.source, 'source').id,
			isSystem: namespace.isSystem ?? false,
		})),
		collections: [],
		primitiveFields: [],
		relationFields: [],
		constraints: [],
		constraintFields: [],
		indexes: [],
		indexFields: [],
	};

	for (const collection of collections) {
		rows.collections.push({
			id: collection.id,
			dbName: string(collection.dbName ?? collection.apiName),
			apiName: string(collection.apiName),
			queryCapabilities: null,
			isSystem: collection.isSystem ?? false,
			namespaceId: collection.namespace === undefined ? null : at(namespaces, collection.namespace, 'namespace').id,
			sourceId: at(sources, collection.source, 'source').id,
		});

		for (const field of collection.primitiveFields) {
			rows.primitiveFields.push({
				id: field.id,
				collectionId: collection.id,
				dbName: string(field.dbName ?? field.apiName),
				apiName: string(field.apiName),
				type: primitiveType(field.type),
				defaultValue: field.defaultValue ?? null,
				isNullable: field.isNullable ?? false,
				isList: field.isList ?? false,
				queryCapabilities: field.isReadonly ? { isReadOnly: true } : null,
			});
		}

		for (const relation of collection.relationFields) {
			const oppositeCollection = at(collections, relation.oppositeCollection, 'collection');

			rows.relationFields.push({
				id: relation.id,
				apiName: string(relation.apiName),
				isNullable: relation.isNullable ?? false,
				isList: relation.isList ?? false,
				oppositeCollectionId: oppositeCollection.id,
				collectionId: collection.id,
				oppositeRelationFieldId: at(oppositeCollection.relationFields, relation.oppositeField, 'relation').id,
				constraintId: relation.constraint?.id ?? null,
			});

			// The constraint rides on the FK side only; its field mappings are
			// pairs of positions into the two collections' primitiveFields.
			const constraint = relation.constraint;

			if (constraint) {
				rows.constraints.push({
					id: constraint.id,
					dbName: string(constraint.dbName),
					onDelete: constraint.onDelete,
					onUpdate: constraint.onUpdate,
				});

				for (const [order, [localField, oppositeField]] of relation.linkingFields.entries()) {
					rows.constraintFields.push({
						order,
						constraintId: constraint.id,
						constrainedFieldId: at(collection.primitiveFields, localField, 'field').id,
						referencedFieldId: at(oppositeCollection.primitiveFields, oppositeField, 'field').id,
					});
				}
			}
		}

		for (const index of collection.indexes ?? []) {
			rows.indexes.push({
				id: index.id,
				dbName: string(index.dbName),
				kind: index.kind,
				collectionId: collection.id,
			});

			for (const [order, fieldIndex] of index.fields.entries()) {
				rows.indexFields.push({
					indexId: index.id,
					fieldId: at(collection.primitiveFields, fieldIndex, 'field').id,
					order,
				});
			}
		}
	}

	return rows;
}

function at<T>(items: T[], index: number, what: string): T {
	const item = items[index];

	if (item === undefined) {
		throw new Error(`Schema manifest references ${what} ${index}, but only ${items.length} exist`);
	}

	return item;
}
